<template>
  <Section
    :id="id"
    class="line-item-entry-section entry-section"
    :header="t('lineItems.lineItems', 2)"
  >
    <template #default>
      <LineItemSummaryCard :line-items="internalValue" />

      <ul role="list" class="line-item-entry-section-list">
        <li v-for="(lineItem, index) in internalValue" :key="index">
          <LineItemEdit
            :ref="(el) => (lineItemEdits[index] = el as LineItemEditComponent)"
            :key="index"
            :model-value="lineItem"
            :disabled="disabled"
            :disable-default-reference-numbers="disableDefaultReferenceNumbers"
            :error-response="serverErrorsArray[index]"
            :handling-units="handlingUnits"
            :index="index"
            :removable="internalValue.length > 1"
            :show-stops="type === 'Shipment'"
            enable-save-product-switch
            expanded
            @duplicate="onDuplicate(index)"
            @remove="onRemove(index)"
            @update:model-value="onUpdate(index, $event)"
          />
        </li>
      </ul>

      <InputError
        v-for="error of allErrors"
        :id="error.$uid"
        :key="error.$uid"
        :error="error"
      />
    </template>

    <template #footer>
      <AddButton
        :disabled="disabled"
        :label="t('lineItems.lineItems')"
        data-testid="line-item-entry-add-line-item-button"
        @click="onAdd"
      />
    </template>
  </Section>
</template>

<script lang="ts" setup>
import type { PropType, Ref } from 'vue';
import { computed, nextTick, onMounted, ref, toRefs } from 'vue';
import { useI18n } from 'vue-i18n';

import {
  useModelValue,
  useScrollAnimations,
  useValidations,
} from '@rygen/rygen-ui/composables';
import { ErrorResponse, ReferenceNumberTypeLevel } from '@rygen/rygen-ui/model';
import { notNull } from '@rygen/rygen-ui/util';

import { useReferenceNumberTypes } from '@/composables';
import { LineItem, OrderLineItem, ShipmentLineItem } from '@/model';
import { useHandlingUnitStore } from '@/stores/HandlingUnit';
import { useParentEntityStore } from '@/stores/ParentEntity';

import LineItemEdit from '@/components/lineItem/LineItemEdit.vue';
import LineItemSummaryCard from '@/components/lineItem/LineItemSummaryCard.vue';
import AddButton from '@/components/ui/AddButton.vue';
import InputError from '@rygen/rygen-ui/components/form/InputError.vue';
import Section from '@rygen/rygen-ui/components/ui/Section.vue';

type LineItemEditComponent = InstanceType<typeof LineItemEdit>;

const props = defineProps({
  modelValue: {
    type: Array as PropType<LineItem[]>,
    default: () => [],
  },
  id: {
    type: String,
    required: true,
  },
  /**
   * A mapping of server errors for each line item
   *
   * The keys have the form `lineItems[{index}].{field}`, where `index` is the
   * index of the stop and `field` is the line item field that has the
   * validation errors.
   * */
  serverErrors: {
    type: Object as PropType<ErrorResponse>,
    default: () => undefined,
  },
  type: {
    type: String as PropType<'Order' | 'Shipment'>,
    required: true,
  },
  disabled: {
    type: Boolean,
    default: () => false,
  },
  disableDefaultReferenceNumbers: {
    type: Boolean,
    default: () => false,
  },
});
const {
  modelValue,
  id,
  serverErrors,
  type,
  disabled,
  disableDefaultReferenceNumbers,
} = toRefs(props);
const emit = defineEmits<{
  (event: 'update:model-value', newVal: LineItem[]): void;
}>();

const { handlingUnits, loadHandlingUnits } = useHandlingUnitStore();
const { t } = useI18n();
const { internalValue } = useModelValue(modelValue, {
  emit,
  factoryMethod(opts: Partial<LineItem[]>) {
    return opts.filter(notNull) ?? [];
  },
});
const { owningClient } = useParentEntityStore();
const {
  getDefaultReferenceNumbers,
  lineItemReferenceNumberTypes,
  loadReferenceNumberTypes,
} = useReferenceNumberTypes();
const { scrollIntoView } = useScrollAnimations();
useValidations(internalValue, {});

const lineItemEdits: Ref<Array<LineItemEditComponent | null>> = ref([]);

const defaultLineItemRefNums = computed(() => {
  return getDefaultReferenceNumbers(lineItemReferenceNumberTypes.value.data);
});

const serverErrorsArray = computed((): Array<ErrorResponse | undefined> => {
  if (!serverErrors?.value?.errorDetails?.fieldErrors) {
    return [];
  }

  // Split the single ErrorResponse into an ErrorResponse for each line item
  const entries = Object.entries(serverErrors.value.errorDetails.fieldErrors);
  return internalValue.value.map((_lineItem, index) => {
    const errorsForLineItem = entries.filter(([field]) =>
      field.includes(`lineItems[${index}]`),
    );
    return serverErrors.value && errorsForLineItem.length
      ? {
          ...serverErrors.value,
          errorDetails: {
            fieldErrors: Object.fromEntries(errorsForLineItem),
          },
        }
      : undefined;
  });
});

const allErrors = computed(() => {
  const numLineItemsWithValidationErrors =
    serverErrorsArray.value.filter(notNull).length;
  if (!numLineItemsWithValidationErrors) {
    return [];
  }

  const error = {
    $uid: 'line-items-validation-error',
    $propertyPath: 'lineItems',
    $property: 'lineItems',
    $message: t('shipments.entry.countParamsHaveValidationErrors', {
      param: t('lineItems.lineItems', numLineItemsWithValidationErrors),
      count: numLineItemsWithValidationErrors,
    }),
    $validator: 'API',
    $params: {},
    $response: undefined,
    $pending: false,
  };
  return [error];
});

const onAdd = async () => {
  const Clazz = type.value === 'Order' ? OrderLineItem : ShipmentLineItem;
  const newLine = new Clazz({
    referenceNumbers: defaultLineItemRefNums.value,
  });
  internalValue.value.push(newLine);

  nextTick(() => {
    const newLineItemIndex = internalValue.value.length - 1;
    const newLineItemEdit = lineItemEdits.value[newLineItemIndex];
    scrollIntoView(newLineItemEdit?.$el);
  });
};

const onDuplicate = (index: number) => {
  const lineItem = internalValue.value[index];

  const Clazz = type.value === 'Order' ? OrderLineItem : ShipmentLineItem;
  const newLineItem = new Clazz(lineItem);

  internalValue.value.push(newLineItem);

  nextTick(() => {
    const newLineItemIndex = internalValue.value.length - 1;
    const newLineItemEdit = lineItemEdits.value[newLineItemIndex];
    scrollIntoView(newLineItemEdit?.$el);
  });
};

const onRemove = (index: number) => {
  internalValue.value.splice(index, 1);
  lineItemEdits.value.splice(index, 1);
};

const onUpdate = (index: number, lineItem: LineItem) => {
  internalValue.value.splice(index, 1, lineItem);
};

const reset = async () => {
  if (internalValue.value.length === 0) {
    const Clazz = type.value === 'Order' ? OrderLineItem : ShipmentLineItem;
    internalValue.value.push(
      new Clazz({ referenceNumbers: defaultLineItemRefNums.value }),
    );
  }
};

onMounted(() => {
  if (!disabled.value && owningClient.value?.id) {
    loadHandlingUnits();
    loadReferenceNumberTypes({ level: ReferenceNumberTypeLevel.LINE_ITEM });
  }
});

defineExpose({
  reset,
});
</script>

<style>
.line-item-entry-section {
  .section-data {
    display: flex;
    flex-direction: column;
    gap: var(--gap-1);

    .line-item-entry-section-list {
      counter-reset: line-item-num;
      display: flex;
      flex-direction: column;
      gap: var(--gap-1);
      list-style: none;
      margin: 0;
      padding: 0;
    }
  }

  .section-footer {
    display: flex;
    align-items: center;
    gap: var(--gap-1);
    margin-top: var(--gap-1);
  }
}
</style>
